import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../../api';
import { useLanguage } from '../../context/LanguageContext';
import StepWelcome from './steps/StepWelcome';
import StepConnect from './steps/StepConnect';
import StepAfterConnect from './steps/StepAfterConnect';
import StepReviewTour from './steps/StepReviewTour';
import StepDone from './steps/StepDone';
import styles from './NewUserWizard.module.css';

// ── Sidebar step dot icon ─────────────────────────────────────────────────────
const CheckSmall = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ── Main Component ────────────────────────────────────────────────────────────
const NewUserWizard = ({ token, userName, initialStep = 0, onClose }) => {
  const { t } = useLanguage();

  const steps = [
    { id: 'welcome',       label: t('stepWelcomeLabel', 'Welcome'),          subDesc: t('stepWelcomeSub', 'Overview of ReplyShoot') },
    { id: 'connect',       label: t('stepConnectLabel', 'Connect Google'),   subDesc: t('stepConnectSub', 'Link your Business Profile') },
    { id: 'how-it-works',  label: t('stepHowItWorksLabel', 'How It Works'),  subDesc: t('stepHowItWorksSub', 'Automated reply flow') },
    { id: 'review-tour',   label: t('stepReviewTourLabel', 'Review Dashboard'), subDesc: t('stepReviewTourSub', 'See your stats & reviews') },
    { id: 'done',          label: t('stepDoneLabel', 'All Done'),          subDesc: t('stepDoneSub', "You're ready to go!") },
  ];

  const [currentStep, setCurrentStep] = useState(() => {
    const resume = localStorage.getItem('wizard_resume_step');
    if (resume !== null) {
      localStorage.removeItem('wizard_resume_step');
      const parsed = parseInt(resume, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed < 5) return parsed;
    }
    return Math.min(Math.max(0, initialStep), 4);
  });

  const [alreadyConnected, setAlreadyConnected] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const contentRef = useRef(null);

  // Check GBP connection status on mount
  useEffect(() => {
    if (!token) return;
    fetch(getApiUrl('/api/auth/google/locations'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const locs = d.locations || [];
        setAlreadyConnected(locs.length > 0);
      })
      .catch(() => {/* silently ignore */});
  }, [token]);

  // Animated step transition
  const goToStep = useCallback((nextStep) => {
    if (nextStep === currentStep) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrentStep(nextStep);
      setTransitioning(false);
      if (contentRef.current) contentRef.current.scrollTop = 0;
    }, 200);
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) goToStep(currentStep + 1);
  }, [currentStep, goToStep, steps.length]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) goToStep(currentStep - 1);
  }, [currentStep, goToStep]);

  const handleClose = useCallback(() => {
    localStorage.setItem('wizard_completed', 'true');
    onClose();
  }, [onClose]);

  // Render the active step content
  const renderStep = () => {
    switch (steps[currentStep].id) {
      case 'welcome':
        return <StepWelcome userName={userName} onNext={handleNext} />;
      case 'connect':
        return (
          <StepConnect
            token={token}
            alreadyConnected={alreadyConnected}
            onNext={handleNext}
          />
        );
      case 'how-it-works':
        return <StepAfterConnect onNext={handleNext} />;
      case 'review-tour':
        return <StepReviewTour onNext={handleNext} />;
      case 'done':
        return <StepDone onClose={handleClose} />;
      default:
        return null;
    }
  };

  const progressPercent = ((currentStep + 1) / steps.length) * 100;
  const isDone = currentStep === steps.length - 1;

  return (
    <div className={styles.wizardOverlay} role="dialog" aria-modal="true" aria-label={t('stepWelcomeLabel', 'Getting started wizard')}>
      <div className={styles.wizardCard}>

        {/* ── Right Sidebar ─────────────────────────────────────────────── */}
        <div className={styles.wizardRight}>
          {/* Brand */}
          <div className={styles.sidebarBrand}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9219a6"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
              <path d="M7 11h10" /><path d="M7 15h6" /><path d="M7 7h8" />
            </svg>
            <span className={styles.sidebarBrandName}>ReplyShoot</span>
          </div>

          {/* Step list */}
          <div className={styles.stepList}>
            {steps.map((step, i) => {
              const isActive = i === currentStep;
              const isDoneStep = i < currentStep;
              const isClickable = isDoneStep; // only allow going back to done steps

              return (
                <div key={step.id}>
                  <div
                    className={`${styles.stepItem} ${isClickable ? styles.stepItemClickable : ''}`}
                    onClick={isClickable ? () => goToStep(i) : undefined}
                    title={isClickable ? `${t('wizardBack', 'Go back to')} "${step.label}"` : undefined}
                  >
                    <div className={styles.stepDotWrap}>
                      <div className={`${styles.stepDot} ${isActive ? styles.stepDotActive : ''} ${isDoneStep ? styles.stepDotDone : ''}`}>
                        {isDoneStep ? <CheckSmall /> : i + 1}
                      </div>
                      {i < steps.length - 1 && (
                        <div className={`${styles.stepConnector} ${isDoneStep ? styles.stepConnectorDone : ''}`} />
                      )}
                    </div>
                    <div className={styles.stepMeta}>
                      <div className={`${styles.stepLabelText} ${isActive ? styles.stepLabelActive : ''} ${isDoneStep ? styles.stepLabelDone : ''}`}>
                        {step.label}
                      </div>
                      <div className={styles.stepSubDesc}>{step.subDesc}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sidebar footer */}
          <div className={styles.sidebarFootNote}>
            {t('wizardNeedHelp', 'Need help?')}{' '}
            <a href="mailto:support@replyshoot.com" style={{ color: '#9219a6', textDecoration: 'none' }}>
              {t('wizardContactUs', 'Contact us')}
            </a>
          </div>
        </div>

        {/* ── Left Content Panel ────────────────────────────────────────── */}
        <div className={styles.wizardLeft}>
          {/* Progress bar */}
          <div className={styles.progressBarWrap}>
            <div className={styles.progressBar} style={{ width: `${progressPercent}%` }} />
          </div>

          {/* Close button */}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label="Close wizard"
            title={t('wizardCloseTooltip', 'Close and come back later (? Help button)')}
          >
            ✕
          </button>

          {/* Step content */}
          <div
            ref={contentRef}
            className={`${styles.wizardContent} ${transitioning ? styles.slideExit : styles.slideEnter}`}
            key={currentStep}
          >
            {renderStep()}
          </div>

          {/* Footer (hidden on last step — it has its own CTA) */}
          {!isDone && (
            <div className={styles.wizardFooter}>
              <div className={styles.footerLeft}>
                {currentStep > 0 && (
                  <button type="button" className={styles.btnBack} onClick={handleBack}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M19 12H5" /><path d="m12 5-7 7 7 7" />
                    </svg>
                    {t('wizardBack', 'Back')}
                  </button>
                )}
              </div>
              <div className={styles.footerRight}>
                <span className={styles.stepCounter}>
                  {t('wizardStepOf', 'Step {current} of {total}').replace('{current}', String(currentStep + 1)).replace('{total}', String(steps.length))}
                </span>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default NewUserWizard;
